<template>
  <form :class="{'opacity-50': isWorking}" autocomplete="on" @submit.prevent="submit">
    <callout v-if="message" :icon="messageIcon" :type="messageType">{{ message }}</callout>
    <form-group
      v-model="firstName"
      v-validate="'required'"
      type="text"
      name="firstName"
      :label="bp('register.firstName')"
      :data-vv-as="bp('register.firstName')"
      :error="getErrorMessage('firstName')"
      show-error-messages
      force-display-error
      @change="resetErrorMessage('firstName')"
    />
    <form-group
      v-model="lastName"
      v-validate="'required'"
      type="text"
      name="lastName"
      :label="bp('register.lastName')"
      :data-vv-as="bp('register.lastName')"
      :error="getErrorMessage('lastName')"
      show-error-messages
      force-display-error
      @change="resetErrorMessage('lastName')"
    />
    <form-group
      v-model="email"
      v-validate="'required|email'"
      type="email"
      name="email"
      :label="bp('register.email')"
      :data-vv-as="bp('register.email')"
      :error="getErrorMessage('email')"
      show-error-messages
      force-display-error
      @change="resetErrorMessage('email')"
    />
    <form-group
      ref="password"
      v-model="password"
      v-validate="'required|checkPassword'"
      type="password"
      name="password"
      :label="bp('register.pwd')"
      :data-vv-as="bp('register.pwd')"
      :error="errors.first('password')"
      show-error-messages
      force-display-error
    />
    <form-group
      v-model="passwordConfirmation"
      v-validate="'required|confirmed:password|checkPassword'"
      type="password"
      name="registerPasswordConfirmation"
      :label="bp('register.checkPwd')"
      :data-vv-as="bp('register.checkPwd')"
      :error="errors.first('registerPasswordConfirmation')"
      show-error-messages
    />
    <div class="text-sm mb-2" v-html="bp('register.emailOptIn')"></div>
    <form-group
      type="checkbox"
      name="emailOptInOptions"
      :multi-items="emailOptInOptions"
      @change="
        (e) => {
          this.$set(this, e.target.name, e.target.checked);
        }
      "
    />
    <div v-if="options.requireGdprPrivacyConsent" class="relative mb-2" qaid="register-privacy-consent">
      <input
        id="register-privacy-consent"
        v-model="hasGdprConsent"
        type="checkbox"
        name="privacyConsent"
        qaid="register-privacy-consent__checkbox"
        class="form__check"
      />
      <span class="form__decorator"></span>
      <label
        class="form__check-label text"
        for="register-privacy-consent"
        qaid="privacy-consent__label"
        v-html="bp('consent.gdpr')"
      >
      </label>
    </div>
    <trek-button
      primary
      type="submit"
      :disabled="isWorking || (options.requireGdprPrivacyConsent && !hasGdprConsent)"
      class="mb-4"
      @click="submit"
    >
      <span v-html="bp('register.submit')"></span>
    </trek-button>
    <div class="text-xs leading-tight text-gray-70">
      <slot name="privacy-notice"></slot>
    </div>
  </form>
</template>
<script>
import get from 'lodash/get';
import Callout from '@/components/Callout';
import FormGroup from '@/components/FormGroup';
import TrekButton from '@/components/TrekButton';
import {initPasswordRules} from '@/components/containers/my-account/utils/password-rules';
import {UserAccountBase} from '@/components/containers/my-account/utils/user-account-base';
import {WithErrorFields} from '@/components/containers/my-account/utils/with-error-fields';

export default {
  name: 'UserRegister',
  components: {Callout, FormGroup, TrekButton},
  extends: UserAccountBase,
  mixins: [WithErrorFields],
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      optInTrek: false,
      optInElectra: false,
      passwordConfirmation: '',
      emailOptInOptions: [],
      titleCode: 'mr',
      hasGdprConsent: false,
    };
  },
  created() {
    this.useCustomPasswordValidationMessages({
      passwordField: 'password',
      passwordConfirmationField: 'registerPasswordConfirmation',
    });
    initPasswordRules(this.bp('register.infoPassword'));
    this.initEmailOptInOptions();
  },
  methods: {
    /**
     * Handle response from API.
     */
    handleResponse(response) {
      const message = get(response.data, 'meta.feedback.message.basePropertyValue');
      if (get(response.data, 'meta.feedback.type') === 'alert') {
        this.errorFields = get(response.data, 'meta.feedback.errorFields', {});
        this.displayError(message);
      } else {
        this.displaySuccess(message);
        this.resetForm();
      }
    },

    /**
     * Reset form data
     */
    resetForm() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.password = '';
      this.optInTrek = true;
      this.optInElectra = true;
      this.passwordConfirmation = '';
    },

    /**
     * Perform signup.
     * @return {Promise<void>}
     */
    async processUpdate() {
      try {
        this.isWorking = true;
        this.errorFields = {};
        this.handleResponse(await this.api.register(this.getFormData()));
      } catch (e) {
        this.displayError(e.message);
      } finally {
        this.isWorking = false;
      }
    },

    /**
     * Get object containing data to be sent to API.
     * @return object
     */
    getFormData() {
      return {
        titleCode: this.titleCode,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        pwd: this.password,
        checkPwd: this.passwordConfirmation,
        CSRFToken: this.options.CSRFToken,
        optin: this.optInTrek,
        optinElectra: this.options.electraOptinToggle && this.optInElectra,
      };
    },

    /**
     * Initialize opt-in options based on toggle status.
     */
    initEmailOptInOptions() {
      this.emailOptInOptions = [];
      const addOption = (displayName, name) => {
        this.emailOptInOptions.push({displayName: this.bp(displayName), name, checked: false, value: true});
      };

      addOption('text.trek', 'optInTrek');
      if (this.options.electraOptinToggle) {
        addOption('text.electra', 'optInElectra');
      }
    },
  },
};
</script>
